import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useFollowUpGuard } from '@/pages/FollowUpReport/guards/useFollowUpGuard'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import { setConsent, setUseCases } from '@/store/api/RunReport/RunReport.slice'

import { businessLocationData } from '../Search/Const/BusinessLocationData'
import { subjectLocationData } from '../Search/Const/SubjectLocationData'

import { EFollowUpStep } from './types'

const HexagonWarningIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M32 4L56 18V46L32 60L8 46V18L32 4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      className="text-neutralColor60"
    />
    <path
      d="M32 22V34"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="text-neutralColor60"
    />
    <circle
      cx="32"
      cy="42"
      r="2"
      fill="currentColor"
      className="text-neutralColor60"
    />
  </svg>
)

export const NewFollowUpReportConsent = () => {
  useFollowUpGuard(EFollowUpStep.Consent)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const currentUseCases = useSelector(selectRunReport).useCases

  const reportId = searchParams.get('reportId') ?? ''
  const locationState = (location.state || {}) as {
    consentEmail?: string
    previousReportHasFCRAUseCase?: boolean
  }
  const consentEmail = locationState.consentEmail || ''

  // If the previous report has FCRA use case,
  // we need to check if the user is compliant to send or not the consent.
  // If the previous report has not FCRA use case,
  // we need to send the consent mandatory.
  const previousReportHasFCRAUseCase =
    locationState.previousReportHasFCRAUseCase || false

  const [selectedSubjectLocation, setSelectedSubjectLocation] =
    useState<string>('')

  const [selectedBusinessLocation, setSelectedBusinessLocation] =
    useState<string>('')

  const handleSubjectLocationChange = (value: string) => {
    setSelectedSubjectLocation(value)
  }

  const handleBusinessLocationChange = (value: string) => {
    setSelectedBusinessLocation(value)
  }

  const handleAcknowledge = () => {
    dispatch(
      setConsent({
        file: null,
        consentEmail: '',
        subjectLocationValue: '',
        subjectLocationName: '',
        businessLocationValue: '',
        businessLocationName: '',
      }),
    )
    dispatch(
      setUseCases({
        ...currentUseCases,
        consentSwitchValue: false,
      }),
    )
    navigate(
      `${AppRoutes.newFollowUpReportLegalDisclaimer}?reportId=${reportId}`,
    )
  }

  const handleSendConsent = () => {
    const selectedLocation = subjectLocationData.find(
      (location) => location.name === selectedSubjectLocation,
    )

    const selectedBusinessLocationData = businessLocationData.find(
      (location) => location.name === selectedBusinessLocation,
    )

    dispatch(
      setConsent({
        file: null,
        consentEmail: consentEmail,
        subjectLocationValue: selectedLocation?.value,
        subjectLocationName: selectedLocation?.name,
        businessLocationValue: selectedBusinessLocationData?.value,
        businessLocationName: selectedBusinessLocationData?.name,
      }),
    )

    navigate(
      `${AppRoutes.newFollowUpReportLegalDisclaimer}?reportId=${reportId}`,
    )
  }

  return (
    <WeScreen useNextButton={false} contentClassName="p-6">
      <div className="flex flex-col items-center justify-center h-full w-full px-4">
        <div className="flex flex-col items-center gap-6 max-w-2xl text-center">
          <HexagonWarningIcon />

          <h1 className="text-3xl font-medium text-neutralColor tracking-wide">
            {t('dashboard.followUp.consent.authorizationRequired')}
          </h1>

          {previousReportHasFCRAUseCase ? (
            <div className="flex flex-col gap-4 mb-10">
              <p className="text-neutralColor60 text-base leading-relaxed">
                {t('dashboard.followUp.consent.certifyText')}
              </p>

              <p className="text-neutralColor60 text-base leading-relaxed">
                {t('dashboard.followUp.consent.cannotCertifyText')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 mb-12">
              <p className="text-neutralColor60 text-base leading-relaxed">
                {t('dashboard.followUp.consent.consentRequiredText')}
              </p>
            </div>
          )}

          <div className="flex flex-row w-full">
            <div className="flex flex-col gap-4 w-full pr-5">
              <label className="flex items-center text-sm font-medium tracking-tight text-neutralColor">
                {t('dashboard.newSearch.consent.subjectLocation')}
              </label>

              <Select onValueChange={handleSubjectLocationChange}>
                <SelectTrigger className="w-full">
                  {selectedSubjectLocation !== ''
                    ? t(selectedSubjectLocation)
                    : t('dashboard.newSearch.consent.subjectLocation')}
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {subjectLocationData?.map((location, index) => (
                      <SelectItem key={index} value={location.name}>
                        {t(location.name)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-4 w-full pl-5">
              <label className="flex items-center text-sm font-medium tracking-tight text-neutralColor">
                {t('dashboard.newSearch.consent.businessLocation')}
              </label>

              <Select onValueChange={handleBusinessLocationChange}>
                <SelectTrigger className="w-full">
                  {selectedBusinessLocation !== ''
                    ? t(selectedBusinessLocation)
                    : t('dashboard.newSearch.consent.businessLocation')}
                </SelectTrigger>
                <SelectContent
                  side="bottom"
                  align="start"
                  avoidCollisions={false}
                >
                  <SelectGroup>
                    {businessLocationData?.map((location, index) => (
                      <SelectItem key={index} value={location.name}>
                        {t(location.name)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-row gap-4 mt-10 mb-10">
            <Button
              variant="outline"
              className="px-8 py-2 h-11 text-neutralColor"
              onClick={handleSendConsent}
              disabled={
                selectedBusinessLocation === '' ||
                selectedSubjectLocation === ''
              }
            >
              {t('dashboard.followUp.consent.sendConsent')}
            </Button>

            {previousReportHasFCRAUseCase && (
              <Button
                className="px-8 py-2 h-11"
                onClick={handleAcknowledge}
                disabled={
                  selectedBusinessLocation === '' ||
                  selectedSubjectLocation === ''
                }
              >
                {t('dashboard.followUp.consent.consentReceived')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full justify-center items-end">
        <p className="flex text-center text-neutralColor60 text-xs font-medium w-3/5 max-md:w-full">
          {t('dashboard.newSearch.consent.wecheckDisclaimer')}
        </p>
      </div>
    </WeScreen>
  )
}
